import React, { useState, useEffect, useRef } from 'react';
import { DollarSign, Clock, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GAMBULL_LOGO = 'https://customer-assets.emergentagent.com/job_source-reveal-3/artifacts/g619rnzz_OIP__24_-removebg-preview.png';
const CROWN_VIDEO = 'https://customer-assets.emergentagent.com/job_web-mods/artifacts/4oy7ku4r_crown.webm';

// Floating images URLs
const FLOATING_IMAGES = [
  'https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/ff80kytq_zeus.png',
  'https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/08vojbo9_ring.png',
  'https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/4093szkr_crown%20%281%29.png',
  'https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/cick3r3k_dog.png',
  'https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/3kgzvuyl_bonus.png',
  'https://customer-assets.emergentagent.com/job_shadingo-blue/artifacts/8mohpwpa_bomb.png'
];

// Floating styles
const floatingStyles = `
  @keyframes float1 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-30px) rotate(5deg); }
  }
  @keyframes float2 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(25px) rotate(-5deg); }
  }
  @keyframes float3 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-25px) rotate(8deg); }
  }
  @keyframes float4 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(30px) rotate(-8deg); }
  }
  @keyframes float5 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(6deg); }
  }
  @keyframes float6 {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(20px) rotate(-6deg); }
  }
  .float-img {
    position: fixed;
    width: 100px;
    height: 100px;
    object-fit: contain;
    pointer-events: auto;
    opacity: 0.6;
    filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.5));
    transition: transform 0.3s ease, filter 0.3s ease;
    cursor: pointer;
    z-index: 5;
  }
  .float-img:hover {
    transform: scale(1.2) !important;
    filter: drop-shadow(0 0 30px rgba(168, 85, 247, 0.8)) drop-shadow(0 10px 30px rgba(0, 0, 0, 0.5));
    z-index: 100;
    opacity: 1;
  }
  .float-1 { top: 15%; left: 2%; animation: float1 6s ease-in-out infinite; }
  .float-2 { top: 15%; right: 2%; animation: float2 5s ease-in-out infinite; }
  .float-3 { top: 50%; left: 2%; animation: float3 7s ease-in-out infinite; }
  .float-4 { top: 50%; right: 2%; animation: float4 5.5s ease-in-out infinite; }
  .float-5 { top: 80%; left: 2%; animation: float5 6.5s ease-in-out infinite; }
  .float-6 { top: 80%; right: 2%; animation: float6 5s ease-in-out infinite; }
`;

// Floating Images Component - hidden on mobile
const FloatingImages = () => (
  <>
    {FLOATING_IMAGES.map((src, idx) => (
      <img
        key={idx}
        src={src}
        alt={`Float ${idx + 1}`}
        className={`float-img float-${idx + 1} hidden md:block`}
      />
    ))}
  </>
);

// Particle component
const Particles = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 15,
    duration: 15 + Math.random() * 20,
    size: 2 + Math.random() * 4,
  }));

  return (
    <div className="particles-container">
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

// Animated Counter
const AnimatedCounter = ({ target, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let current = 0;
          const increment = target / 60;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, 30);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, hasAnimated]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

// Skeleton
const Skeleton = ({ width = '100%', height = '20px', className = '' }) => (
  <div className={`animate-pulse bg-white/10 rounded ${className}`} style={{ width, height }} />
);

// Countdown Timer
const CountdownTimer = ({ timeRemaining }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  // Always update from backend when timeRemaining changes
  useEffect(() => {
    if (timeRemaining && timeRemaining.days !== undefined) {
      // Always use backend data - clear any old cached data
      localStorage.removeItem('leaderboard_timer');
      localStorage.removeItem('leaderboard_timer_timestamp');
      setTimeLeft(timeRemaining);
    }
  }, [timeRemaining]);

  // Countdown interval
  useEffect(() => {
    if (!timeLeft) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (!prev) return prev;
        let { days, hours, minutes, seconds } = prev;
        if (seconds > 0) seconds--;
        else if (minutes > 0) { minutes--; seconds = 59; }
        else if (hours > 0) { hours--; minutes = 59; seconds = 59; }
        else if (days > 0) { days--; hours = 23; minutes = 59; seconds = 59; }
        return { days, hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft !== null]);

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="bg-black/60 border border-purple-500/40 rounded-lg px-2 sm:px-4 py-2 sm:py-3 min-w-[50px] sm:min-w-[70px]">
        <span className="text-xl sm:text-3xl font-oxanium font-bold text-purple-400">{String(value || 0).padStart(2, '0')}</span>
      </div>
      <span className="text-[10px] sm:text-xs font-exo text-white/60 mt-1 uppercase">{label}</span>
    </div>
  );

  const displayTime = timeLeft || { days: 0, hours: 0, minutes: 0, seconds: 0 };

  return (
    <div className="flex items-center justify-center gap-1 sm:gap-3 my-4 sm:my-8 flex-wrap">
      <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 animate-pulse" />
      <TimeBlock value={displayTime.days} label="Days" />
      <span className="text-xl sm:text-2xl text-purple-400 font-bold">:</span>
      <TimeBlock value={displayTime.hours} label="Hours" />
      <span className="text-xl sm:text-2xl text-purple-400 font-bold">:</span>
      <TimeBlock value={displayTime.minutes} label="Mins" />
      <span className="text-xl sm:text-2xl text-purple-400 font-bold">:</span>
      <TimeBlock value={displayTime.seconds} label="Secs" />
    </div>
  );
};

// Premium Leaderboard Card Component
const LeaderCard = ({ player, rank, prize, isLoading }) => {
  const rankColors = {
    1: {
      gradient: 'from-yellow-600/40 via-yellow-500/20 to-yellow-900/40',
      border: 'border-yellow-500',
      glow: 'shadow-[0_0_60px_rgba(234,179,8,0.3)]',
      hexColor: '#FFD700',
      ribbonBg: 'bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400',
      ribbonText: 'text-black'
    },
    2: {
      gradient: 'from-gray-500/40 via-gray-400/20 to-gray-700/40',
      border: 'border-gray-400',
      glow: 'shadow-[0_0_40px_rgba(156,163,175,0.3)]',
      hexColor: '#C0C0C0',
      ribbonBg: 'bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300',
      ribbonText: 'text-black'
    },
    3: {
      gradient: 'from-orange-700/40 via-orange-500/20 to-orange-900/40',
      border: 'border-orange-500',
      glow: 'shadow-[0_0_40px_rgba(234,88,12,0.3)]',
      hexColor: '#CD7F32',
      ribbonBg: 'bg-gradient-to-r from-orange-400 via-orange-300 to-orange-400',
      ribbonText: 'text-black'
    }
  };

  const config = rankColors[rank];

  return (
    <div 
      className={`relative flex h-[380px] w-[280px] flex-col items-center rounded-2xl bg-gradient-to-b ${config.gradient} p-6 ${config.glow} border ${config.border} backdrop-blur-sm`}
      style={{ 
        background: `linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.95) 100%)`,
        boxShadow: `0 0 60px ${config.hexColor}33, inset 0 1px 0 ${config.hexColor}44`
      }}
    >
      {/* Floating Crown Video for Rank 1 - centered on card */}
      {rank === 1 && (
        <div className="absolute -top-8 w-full flex justify-center z-20">
          <video 
            src={CROWN_VIDEO} 
            autoPlay 
            loop 
            muted
            playsInline 
            className="w-20"
            style={{ 
              animation: 'float 3s ease-in-out infinite',
              filter: 'drop-shadow(0 0 15px rgba(255,215,0,0.5))'
            }}
          />
        </div>
      )}

      {/* Avatar Section */}
      <div className={`relative mt-${rank === 1 ? '4' : '8'}`}>
        {/* Outer Ring */}
        <div 
          className="rounded-full p-1"
          style={{ 
            background: `linear-gradient(135deg, ${config.hexColor}, ${config.hexColor}66)`,
            boxShadow: `0 0 30px ${config.hexColor}66`
          }}
        >
          {/* Inner Avatar */}
          <div className="w-[90px] h-[90px] rounded-full overflow-hidden bg-black border-2 border-black">
            {isLoading || !player ? (
              <div className="w-full h-full bg-white/10 animate-pulse" />
            ) : (
              <img 
                src={player.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${player.username}`} 
                alt={player.username}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </div>
        
        {/* Rank Hex Badge */}
        <div 
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-12 h-14 flex items-center justify-center"
          style={{
            background: `linear-gradient(180deg, ${config.hexColor} 0%, ${config.hexColor}88 100%)`,
            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
            boxShadow: `0 4px 20px ${config.hexColor}66`
          }}
        >
          <span className="text-black font-bold text-xl mt-1">{rank}</span>
        </div>
      </div>

      {/* Username */}
      <div className="mt-10 text-center">
        {isLoading || !player ? (
          <Skeleton width="140px" height="24px" className="mx-auto" />
        ) : (
          <h2 className="text-xl font-orbitron font-semibold text-white uppercase tracking-wide truncate w-44">
            {player.username}
          </h2>
        )}
      </div>

      {/* Wagered Section */}
      <div className="mt-4 text-center">
        <h3 className="text-sm font-exo uppercase text-white/60 tracking-wider">Wagered</h3>
        {isLoading || !player ? (
          <Skeleton width="120px" height="28px" className="mx-auto mt-1" />
        ) : (
          <h3 className="text-xl font-oxanium font-semibold mt-1">
            <span className="text-green-400">$</span>
            <span className="text-white">{Math.floor(player.wagered).toLocaleString()}</span>
            <span className="text-white/50">.{((player.wagered % 1) * 100).toFixed(0).padStart(2, '0')}</span>
          </h3>
        )}
      </div>

      {/* Prize Ribbon */}
      <div className="absolute bottom-4 left-0 right-0 mx-auto w-[220px]">
        <div 
          className={`relative ${config.ribbonBg} py-2 px-4 text-center`}
          style={{
            clipPath: 'polygon(10% 0%, 90% 0%, 100% 50%, 90% 100%, 10% 100%, 0% 50%)',
            boxShadow: `0 4px 20px ${config.hexColor}44`
          }}
        >
          <h1 className={`text-2xl font-orbitron font-bold ${config.ribbonText}`}>
            ${prize.toLocaleString()}
          </h1>
        </div>
        {/* Ribbon Tails */}
        <div className="absolute -bottom-2 left-2 w-6 h-4 bg-gradient-to-b from-yellow-600 to-yellow-800" 
          style={{ clipPath: 'polygon(0 0, 100% 0, 50% 100%)', opacity: rank === 1 ? 1 : 0 }} />
        <div className="absolute -bottom-2 right-2 w-6 h-4 bg-gradient-to-b from-yellow-600 to-yellow-800" 
          style={{ clipPath: 'polygon(0 0, 100% 0, 50% 100%)', opacity: rank === 1 ? 1 : 0 }} />
      </div>
    </div>
  );
};

const Leaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Updated prizes: $250 total
  const prizes = { 1: 150, 2: 50, 3: 25, 4: 10, 5: 5, 6: 5, 7: 5 };

  useEffect(() => {
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/leaderboard`);
      setLeaderboardData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard data');
      setLeaderboardData({ players: [], total_players: 0, has_data: false, time_remaining: null });
    } finally {
      setLoading(false);
    }
  };

  const players = leaderboardData?.players || [];
  const hasData = leaderboardData?.has_data || false;
  const raceStatus = leaderboardData?.race_status || 'running';
  const tableData = players.slice(3);

  // Show race paused/stopped message
  if (raceStatus === 'paused' || raceStatus === 'stopped') {
    return (
      <div className="min-h-screen hero-gradient pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-6 relative overflow-hidden">
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <img 
            src={GAMBULL_LOGO} 
            alt="GamBull" 
            className="h-16 md:h-20 mx-auto mb-6"
          />
          <h1 className="text-4xl md:text-6xl font-orbitron font-bold mb-6">
            <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-purple-600 bg-clip-text text-transparent">
              LEADERBOARD
            </span>
          </h1>
          <div className={`p-8 rounded-2xl border-2 ${
            raceStatus === 'paused' 
              ? 'bg-yellow-900/20 border-yellow-500' 
              : 'bg-red-900/20 border-red-500'
          }`}>
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              raceStatus === 'paused' ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {raceStatus === 'paused' ? 'Race Paused' : 'Race Ended'}
            </h2>
            <p className="text-lg text-white/70">
              {raceStatus === 'paused' 
                ? 'The leaderboard race is currently paused. Check back soon!' 
                : 'This leaderboard race has ended. Stay tuned for the next one!'}
            </p>
          </div>
          {leaderboardData?.time_remaining && (
            <div className="mt-8">
              <p className="text-white/50">Next race starts in:</p>
              <CountdownTimer timeRemaining={leaderboardData.time_remaining} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-6 relative overflow-hidden">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes crownFloat {
          0%, 100% { transform: translateY(0px) rotate(-3deg); }
          50% { transform: translateY(-12px) rotate(3deg); }
        }
        .crown-float {
          animation: crownFloat 2.5s ease-in-out infinite;
        }
        ${floatingStyles}
      `}</style>
      
      {/* Floating Images */}
      <FloatingImages />
      
      <Particles />
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <img 
            src={GAMBULL_LOGO} 
            alt="GamBull" 
            className="h-16 md:h-24 lg:h-28 mx-auto mb-3 md:mb-4"
          />
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-oxanium font-bold text-yellow-400 mb-2">
            <AnimatedCounter target={250} prefix="$" suffix=" Prize Pool" />
          </h2>
          
          <p className="text-base md:text-lg font-exo text-white/60 mb-4 md:mb-6">Monthly</p>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-orbitron font-bold mb-3 md:mb-4">
            <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-purple-600 bg-clip-text text-transparent">
              LEADERBOARD
            </span>
          </h1>
          
          <p className="text-base md:text-xl font-exo text-white/70">Compete with the best and climb to the top!</p>
        </div>

        <CountdownTimer timeRemaining={leaderboardData?.time_remaining} />

        {/* Error State */}
        {!loading && error && !hasData && (
          <div className="text-center py-8 md:py-12">
            <AlertCircle className="w-12 h-12 md:w-16 md:h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl md:text-2xl font-orbitron text-white mb-2">No Data Available</h3>
            <p className="text-white/60 font-exo text-sm md:text-base">No players have participated yet in this period.</p>
          </div>
        )}

        {/* Top 3 Podium - Premium Cards */}
        <div className="flex justify-center items-end gap-3 md:gap-6 mb-8 md:mb-16 mt-6 md:mt-8 flex-wrap px-2">
          {/* 2nd Place */}
          <div className="order-1 md:order-1 scale-90 md:scale-100">
            <LeaderCard player={players[1]} rank={2} prize={prizes[2]} isLoading={loading} />
          </div>
          
          {/* 1st Place */}
          <div className="order-0 md:order-2 -mt-4 md:-mt-8 scale-95 md:scale-100">
            <LeaderCard player={players[0]} rank={1} prize={prizes[1]} isLoading={loading} />
          </div>
          
          {/* 3rd Place */}
          <div className="order-2 md:order-3 scale-90 md:scale-100">
            <LeaderCard player={players[2]} rank={3} prize={prizes[3]} isLoading={loading} />
          </div>
        </div>

        {/* Leaderboard Table */}
        <Card className="bg-black/40 backdrop-blur-md border-purple-500/30 overflow-hidden">
          <div className="p-4 md:p-8">
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-4 pb-3 md:pb-4 mb-4 md:mb-6 border-b border-purple-500/30">
              <div className="text-purple-400 font-orbitron font-semibold text-sm md:text-lg">Rank</div>
              <div className="text-purple-400 font-orbitron font-semibold text-sm md:text-lg md:col-span-2">Player</div>
              <div className="text-purple-400 font-orbitron font-semibold text-sm md:text-lg text-right">Wager</div>
            </div>

            <div className="space-y-2 md:space-y-3">
              {loading ? (
                [4, 5, 6, 7, 8, 9, 10].map(rank => (
                  <div key={rank} className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-4 p-2 md:p-4 rounded-xl">
                    <div className="flex items-center">
                      <span className="text-lg md:text-2xl font-oxanium font-bold text-purple-400/50">#{rank}</span>
                    </div>
                    <div className="md:col-span-2 flex items-center gap-2 md:gap-4">
                      <Skeleton width="36px" height="36px" className="rounded-full md:w-12 md:h-12" />
                      <div>
                        <Skeleton width="80px" height="16px" className="mb-1 md:w-[120px] md:h-[20px]" />
                        {prizes[rank] && <Skeleton width="60px" height="12px" className="md:w-[80px] md:h-[14px]" />}
                      </div>
                    </div>
                    <div className="flex items-center justify-end">
                      <Skeleton width="70px" height="20px" className="md:w-[100px] md:h-[24px]" />
                    </div>
                  </div>
                ))
              ) : tableData.length > 0 ? (
                tableData.map((player) => (
                  <div 
                    key={player.rank}
                    className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-4 p-2 md:p-4 rounded-xl transition-all duration-300 hover:scale-[1.01] hover:bg-purple-500/10 cursor-pointer border border-transparent hover:border-purple-500/20"
                  >
                    <div className="flex items-center">
                      <span className="text-lg md:text-2xl font-oxanium font-bold text-purple-400">#{player.rank}</span>
                    </div>
                    <div className="md:col-span-2 flex items-center gap-2 md:gap-4">
                      <img src={player.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${player.username}`} alt={player.username} className="w-9 h-9 md:w-12 md:h-12 rounded-full border-2 border-purple-500/50 object-cover flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-white font-exo font-medium text-sm md:text-lg block truncate">{player.username}</span>
                        {prizes[player.rank] && <span className="text-yellow-400 font-oxanium text-xs md:text-sm">${prizes[player.rank]}</span>}
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-0.5 md:gap-1">
                      <span className="text-green-400 font-oxanium font-bold text-base md:text-xl">${player.wagered.toLocaleString()}</span>
                    </div>
                  </div>
                ))
              ) : (
                [4, 5, 6, 7].map(rank => (
                  <div key={rank} className="grid grid-cols-4 gap-4 p-4 rounded-xl opacity-50">
                    <div className="flex items-center">
                      <span className="text-2xl font-oxanium font-bold text-purple-400/50">#{rank}</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full border-2 border-purple-500/30 bg-black/40 flex items-center justify-center">
                        <span className="text-white/30 text-2xl">-</span>
                      </div>
                      <div>
                        <span className="text-white/50 font-exo font-medium text-lg block">-</span>
                        {prizes[rank] && <span className="text-yellow-400/50 font-oxanium text-sm">Prize: ${prizes[rank]}</span>}
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <span className="text-green-400/50 font-oxanium font-bold text-xl">$0</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Card>

        <div className="mt-12 text-center">
          <p className="text-white/60 font-exo text-lg">Rankings update every 10 minutes. Keep wagering to climb higher!</p>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
